import React from 'react';
import { Badge, Modal } from 'react-bootstrap';
import AiStatusDot from './AiStatusDot';
import { stateDescriptor, outcomeDescriptor } from '../../../Services/AiActivityService';
import { formatDateTime } from '../../../Utils/timezone';

/**
 * ONE DECISION, OPENED FROM THE ACTIVITY TABLE.
 *
 * Four questions, in the order somebody asks them: what it saw, what it
 * produced, what produced it, and what a person did about it. The table
 * answers "which row"; this answers "and was it right".
 *
 * NOTHING HERE IS A CONTROL. The row is append-only and this phase is
 * read-only, so there is no approve, reject or re-run button — a button that
 * cannot act is worse than no button. The override section reports an
 * override that already happened; it does not offer to make one.
 *
 * ── WHAT IT SAW IS A DIGEST, NOT THE INPUT ──
 * `inputs_digest` is a sha256 of the input and the input itself is never
 * stored — enough to tell "the same paste twice" from "two different pastes"
 * without this collection becoming a copy of every customer email the office
 * has handled. So this section shows the subject, the contexts the run
 * actually read, and the digest. Saying so plainly beats implying the prompt
 * is here somewhere.
 *
 * ── `output` IS NEVER OVERWRITTEN BY `override` ──
 * They are two facts and they are rendered as two. What the model said is the
 * only label this collection exists to learn from; replacing it with the
 * correction would throw that away.
 */

/** A context or trigger key, read as words: `email-inbox` → `Email inbox`. */
const humanise = (key) => {
  const words = String(key).replace(/[-_]/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** `—` rather than a blank, so an absent fact reads as absent. */
const or = (value) => (value === 0 || value ? value : '—');

/**
 * `instruction_rev` has THREE answers, not two.
 *
 * `null` is a recorded fact — the function read the instruction store and
 * nothing was published — and that is a different answer from the field being
 * absent, which means the function is not steerable at all. One of the ten is
 * steerable today, so the common case is absence and it must not read as
 * "nothing was published".
 */
const instructionRev = (decision) => {
  if (!Object.prototype.hasOwnProperty.call(decision, 'instruction_rev')) {
    return 'Not steerable';
  }
  return decision.instruction_rev === null
    ? 'Nothing published'
    : `v${decision.instruction_rev}`;
};

/** A `Mixed` field, shown as it was stored. The shape is the function's own. */
const asJson = (value) => JSON.stringify(value, null, 2);

function Field({ label, children }) {
  return (
    <div className="ai-decision-field">
      <span className="ai-panel-label">{label}</span>
      <span className="ai-decision-value">{children}</span>
    </div>
  );
}

function AiDecisionModal({ decision, functionLabel, onHide }) {
  const state = stateDescriptor(decision.state);
  const outcome = outcomeDescriptor(decision.tool_outcome);
  const contexts = decision.contexts_read || [];
  const override = decision.override || null;
  const hasOutput = decision.output !== undefined && decision.output !== null;

  return (
    <Modal show size="lg" onHide={onHide} scrollable>
      <Modal.Header closeButton>
        <Modal.Title as="h2" className="h5 mb-0">
          {functionLabel || decision.ai_function}
          <span className="ai-decision-when">{formatDateTime(decision.occurred_at)}</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="ai-decision">
        <div className="ai-decision-head">
          <span className="ai-state">
            {state && <AiStatusDot tone={state.tone} />}
            {state ? state.label : '—'}
          </span>
          {outcome && outcome.value !== 'ok' && (
            <Badge bg="danger">{outcome.label}</Badge>
          )}
          {decision.capability === 'act' && <Badge bg="warning" text="dark">Acts</Badge>}
        </div>

        {decision.error && (
          <p className="ai-decision-error">{decision.error}</p>
        )}

        <section className="ai-panel">
          <div className="ai-panel-title">What it saw</div>
          <Field label="Subject">
            {decision.subject?.reference || decision.subject?.id || '—'}
            {decision.subject?.kind && decision.subject.kind !== 'none'
              && <span className="text-muted">{` · ${humanise(decision.subject.kind)}`}</span>}
          </Field>
          <Field label="Contexts read">
            {contexts.length === 0
              ? 'None'
              : contexts.map((key) => (
                <Badge bg="light" text="dark" className="me-1" key={key}>{humanise(key)}</Badge>
              ))}
          </Field>
          <Field label="Input digest">
            {decision.inputs_digest
              ? <code>{decision.inputs_digest}</code>
              : '—'}
          </Field>
        </section>

        <section className="ai-panel">
          <div className="ai-panel-title">What it produced</div>
          {hasOutput
            ? <pre className="ai-decision-json">{asJson(decision.output)}</pre>
            : <p className="mb-0">Nothing was recorded.</p>}
          <Field label="Confidence">
            {decision.confidence === 0 || decision.confidence
              ? `${Math.round(decision.confidence * 100)}%`
              : '—'}
          </Field>
          <Field label="Changed">{or(decision.action_taken)}</Field>
        </section>

        <section className="ai-panel">
          <div className="ai-panel-title">What produced it</div>
          <Field label="Model">{decision.model ? <code>{decision.model}</code> : '—'}</Field>
          <Field label="Prompt version">{or(decision.prompt_version)}</Field>
          <Field label="Instruction revision">{instructionRev(decision)}</Field>
          <Field label="Started by">
            {decision.trigger ? humanise(decision.trigger) : '—'}
            {decision.actor?.email && <span className="text-muted">{` · ${decision.actor.email}`}</span>}
          </Field>
          <Field label="Took">
            {decision.duration_ms ? `${decision.duration_ms} ms` : '—'}
          </Field>
          <Field label="Tokens">
            {decision.tokens?.input || decision.tokens?.output
              ? `${or(decision.tokens?.input)} in · ${or(decision.tokens?.output)} out`
              : '—'}
            {decision.cost_usd ? ` · $${decision.cost_usd}` : ''}
          </Field>
        </section>

        <section className="ai-panel">
          <div className="ai-panel-title">What a person did</div>
          {override ? (
            <>
              <Field label="Changed by">
                {or(override.by)}
                {override.at && <span className="text-muted">{` · ${formatDateTime(override.at)}`}</span>}
              </Field>
              <Field label="Reason">{or(override.reason)}</Field>
              <Field label="From">
                <pre className="ai-decision-json">{asJson(override.from)}</pre>
              </Field>
              <Field label="To">
                <pre className="ai-decision-json">{asJson(override.to)}</pre>
              </Field>
            </>
          ) : (
            <p className="mb-0">Nobody has changed this.</p>
          )}
        </section>
      </Modal.Body>
    </Modal>
  );
}

export default AiDecisionModal;
